import { DateTime } from 'luxon';
import {
  calculateLightThresholds,
  convertThresholdsToTimes,
  handleImmediateActionSafeguard,
  isIrrigationActuator,
  supportsImmediateTrigger,
} from './actionUtils';
import { publishMQTTStatus } from './mqttUtils';

export const QUICK_ACTION_RECIPES = {
  EXTEND_LIGHTS: 'extend_lights',
  RESTORE_LIGHTS: 'restore_lights',
  RUN_NOW: 'run_now',
};

export const QUICK_ACTION_TYPE_FILTERS = [
  { id: 'light', label: 'All lights', actuatorTypes: ['light'], recipe: QUICK_ACTION_RECIPES.EXTEND_LIGHTS },
  {
    id: 'irrigation',
    label: 'All irrigation',
    actuatorTypes: ['irrigation', 'irrigation2'],
    recipe: QUICK_ACTION_RECIPES.RUN_NOW,
  },
  { id: 'fan', label: 'All fans', actuatorTypes: ['fan'], recipe: QUICK_ACTION_RECIPES.RUN_NOW },
  { id: 'humidity', label: 'All humidifiers', actuatorTypes: ['humidity'], recipe: QUICK_ACTION_RECIPES.RUN_NOW },
];

export const QUICK_ACTION_PRESET_RECIPES = [
  {
    value: QUICK_ACTION_RECIPES.EXTEND_LIGHTS,
    label: 'Extend lights',
    executionRecipe: QUICK_ACTION_RECIPES.EXTEND_LIGHTS,
    actuatorTypes: ['light'],
    placeholder: 'Grow shelf lights',
  },
  {
    value: 'run_irrigation',
    label: 'Run irrigation',
    executionRecipe: QUICK_ACTION_RECIPES.RUN_NOW,
    actuatorTypes: ['irrigation', 'irrigation2'],
    hasIrrigationOptions: true,
    placeholder: 'Shelf irrigation',
  },
  {
    value: 'run_fan',
    label: 'Run fans',
    executionRecipe: QUICK_ACTION_RECIPES.RUN_NOW,
    actuatorTypes: ['fan'],
    placeholder: 'Ventilation fans',
  },
  {
    value: 'run_humidity',
    label: 'Run humidifiers',
    executionRecipe: QUICK_ACTION_RECIPES.RUN_NOW,
    actuatorTypes: ['humidity'],
    placeholder: 'Humidifiers',
  },
];

const LEGACY_MIXED_RUN_NOW_PRESET = {
  value: QUICK_ACTION_RECIPES.RUN_NOW,
  label: 'Run now (mixed)',
  executionRecipe: QUICK_ACTION_RECIPES.RUN_NOW,
  actuatorTypes: ['irrigation', 'irrigation2', 'fan', 'humidity'],
};

export function getPresetRecipeDefinition(presetRecipe) {
  return QUICK_ACTION_PRESET_RECIPES.find((item) => item.value === presetRecipe)
    || (presetRecipe === QUICK_ACTION_RECIPES.RUN_NOW
      ? LEGACY_MIXED_RUN_NOW_PRESET
      : QUICK_ACTION_PRESET_RECIPES[0]);
}

export function getPresetRecipeOptions(presetRecipe) {
  if (presetRecipe === QUICK_ACTION_RECIPES.RUN_NOW) {
    return [...QUICK_ACTION_PRESET_RECIPES, LEGACY_MIXED_RUN_NOW_PRESET];
  }
  return QUICK_ACTION_PRESET_RECIPES;
}

export function getExecutionRecipe(presetRecipe) {
  return getPresetRecipeDefinition(presetRecipe).executionRecipe;
}

export function isExtendLightsPreset(presetRecipe) {
  return getExecutionRecipe(presetRecipe) === QUICK_ACTION_RECIPES.EXTEND_LIGHTS;
}

export const DEFAULT_LIGHT_EXTEND_OPTIONS = {
  endTime: '23:00',
  timezone: 'America/Chicago',
};

export const DEFAULT_RUN_NOW_OPTIONS = {
  duration: 4,
  sensorTracking: true,
};

export function normalizePresetOptions(presetRecipe, options = {}) {
  const definition = getPresetRecipeDefinition(presetRecipe);

  if (definition.executionRecipe === QUICK_ACTION_RECIPES.RUN_NOW) {
    return {
      ...DEFAULT_RUN_NOW_OPTIONS,
      ...options,
      duration: Number(options.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration),
      sensorTracking: options.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking,
    };
  }

  return {
    ...DEFAULT_LIGHT_EXTEND_OPTIONS,
    ...options,
  };
}

export function describePresetOptions(preset, actions = []) {
  if (!preset) return '';

  const definition = getPresetRecipeDefinition(preset.recipe);

  if (definition.executionRecipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS) {
    return `Extends until ${formatExtendLightsUntilLabel(
      preset.options || {},
      selectActionsByPreset(preset, actions)
    )}`;
  }

  if (definition.hasIrrigationOptions) {
    const duration = preset.options?.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration;
    const sensorTracking = preset.options?.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking;
    return `Irrigation ${duration}s${sensorTracking ? ', sensor-guided' : ', schedule only'}`;
  }

  if (definition.value === 'run_fan') {
    return 'Immediate fan trigger';
  }

  if (definition.value === 'run_humidity') {
    return 'Immediate humidifier trigger';
  }

  if (definition.value === QUICK_ACTION_RECIPES.RUN_NOW) {
    const selected = selectActionsByPreset(preset, actions);
    const hasIrrigation = selected.some((action) => isIrrigationActuator(action.actuatorType));
    const hasFan = selected.some((action) => action.actuatorType === 'fan');
    const hasHumidity = selected.some((action) => action.actuatorType === 'humidity');
    const parts = [];

    if (hasIrrigation) {
      const duration = preset.options?.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration;
      const sensorTracking = preset.options?.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking;
      parts.push(`Irrigation ${duration}s${sensorTracking ? ', sensor-guided' : ', schedule only'}`);
    }
    if (hasFan || hasHumidity) {
      parts.push('Immediate trigger for fans/humidifiers');
    }

    return parts.join(' · ') || 'Immediate trigger on selected devices';
  }

  return '';
}

const EMPTY_THRESHOLDS = {
  humidity: { low: 'X', high: 'X' },
  temperature: { low: 'X', high: 'X' },
  water: { low: 'X', high: 'X' },
  water2: { low: 'X', high: 'X' },
  light: { low: 'X', high: 'X' },
};

export function findSensorCardForAction(action, cards = []) {
  return cards.find((card) => card.macAddress === action?.sensor) || null;
}

export function getActionDisplayName(action) {
  return action?.actuatorDescription || action?.macAddress || 'Action device';
}

export function buildThresholdsFromCard(card, lightOverride = null) {
  return {
    humidity: { low: card?.humidityLow ?? 'X', high: card?.humidityHigh ?? 'X' },
    temperature: { low: card?.temperatureLow ?? 'X', high: card?.temperatureHigh ?? 'X' },
    water: { low: card?.waterLow ?? 'X', high: card?.waterHigh ?? 'X' },
    water2: { low: card?.water2Low ?? 'X', high: card?.water2High ?? 'X' },
    light: lightOverride || { low: card?.lightLow ?? 'X', high: card?.lightHigh ?? 'X' },
  };
}

function getExistingLightStartTime(sensorCard, timezone) {
  const low = Number.parseInt(sensorCard?.lightLow, 10);
  const high = Number.parseInt(sensorCard?.lightHigh, 10);
  const hasExistingSchedule = Number.isFinite(low) && Number.isFinite(high);

  if (hasExistingSchedule) {
    return convertThresholdsToTimes(low, high, timezone).startTime;
  }

  return DateTime.now().setZone(timezone).minus({ minutes: 1 }).toFormat('HH:mm');
}

export function getCurrentLightWindow(options = DEFAULT_LIGHT_EXTEND_OPTIONS, sensorCard = null) {
  const timezone = options.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone;
  const now = DateTime.now().setZone(timezone);
  const start = getExistingLightStartTime(sensorCard, timezone);
  let endTime = options.endTime || DEFAULT_LIGHT_EXTEND_OPTIONS.endTime;

  const end = DateTime.fromFormat(endTime, 'HH:mm', { zone: timezone });
  if (!end.isValid) {
    endTime = now.plus({ hours: 4 }).toFormat('HH:mm');
  } else {
    const endMinutes = end.hour * 60 + end.minute;
    const nowMinutes = now.hour * 60 + now.minute;
    // Equal clock time has just been reached, so keep the light on a bit longer.
    // An earlier clock time is tomorrow morning (11 PM now, 1 AM end), so leave it.
    if (endMinutes === nowMinutes) {
      endTime = now.plus({ hours: 4 }).toFormat('HH:mm');
    }
  }

  return {
    startTime: start,
    endTime,
    timezone,
    thresholds: calculateLightThresholds(start, endTime, timezone),
  };
}

export function snapshotLightActions(actions = [], cards = []) {
  return actions.reduce((snapshot, action) => {
    const sensorCard = findSensorCardForAction(action, cards);
    if (action.actuatorType !== 'light' || !sensorCard) return snapshot;

    snapshot[action.macAddress] = {
      actionMac: action.macAddress,
      sensorMac: sensorCard.macAddress,
      lightLow: sensorCard.lightLow,
      lightHigh: sensorCard.lightHigh,
      timezone: action.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone,
    };
    return snapshot;
  }, {});
}

const TIMEZONE_DISPLAY_LABELS = {
  UTC: 'UTC',
  'America/Chicago': 'Central Time',
  'America/New_York': 'Eastern Time',
};

function formatTimezoneLabel(timezone = DEFAULT_LIGHT_EXTEND_OPTIONS.timezone) {
  return TIMEZONE_DISPLAY_LABELS[timezone]
    || timezone.split('/').pop()?.replace(/_/g, ' ')
    || timezone;
}

export function formatLightScheduleTime(time, timezone = DEFAULT_LIGHT_EXTEND_OPTIONS.timezone) {
  const zone = timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone;
  const parsed = DateTime.fromFormat(time, 'HH:mm', { zone });
  if (!parsed.isValid) {
    return time;
  }
  return parsed.toFormat('h:mm a').replace(/\s+(AM|PM)/i, '$1');
}

function resolveExtendTimezone(options = {}, targetActions = []) {
  if (options.timezone) return options.timezone;

  const actionTimezones = [...new Set(
    targetActions
      .filter((action) => action.actuatorType === 'light' && action.timezone)
      .map((action) => action.timezone)
  )];

  if (actionTimezones.length === 1) return actionTimezones[0];
  return DEFAULT_LIGHT_EXTEND_OPTIONS.timezone;
}

export function formatExtendLightsUntilLabel(options = {}, targetActions = []) {
  const endTime = options.endTime || DEFAULT_LIGHT_EXTEND_OPTIONS.endTime;
  const timezone = resolveExtendTimezone(options, targetActions);
  const formattedTime = formatLightScheduleTime(endTime, timezone);
  const tzLabel = formatTimezoneLabel(timezone);
  return `${formattedTime} ${tzLabel}`;
}

export function describeQuickActionSummary(recipe, options = {}, label = '', targetActions = []) {
  if (recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS) {
    return `Extend lights until ${formatExtendLightsUntilLabel(options, targetActions)}`;
  }

  if (recipe === QUICK_ACTION_RECIPES.RESTORE_LIGHTS) {
    return label || 'Restore saved light schedules';
  }

  if (recipe === QUICK_ACTION_RECIPES.RUN_NOW) {
    const types = new Set(targetActions.map((action) => action.actuatorType));
    const hasIrrigation = [...types].some((type) => isIrrigationActuator(type));
    const hasFan = types.has('fan');
    const hasHumidity = types.has('humidity');

    if (hasIrrigation && !hasFan && !hasHumidity) {
      const duration = Number(options.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration);
      const sensorTracking = options.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking;
      return `Irrigation ${duration}s${sensorTracking ? ', sensor-guided' : ', schedule only'}`;
    }

    if (hasFan && !hasIrrigation && !hasHumidity) {
      return 'Immediate fan trigger';
    }

    if (hasHumidity && !hasIrrigation && !hasFan) {
      return 'Immediate humidifier trigger';
    }

    const parts = [];
    if (hasIrrigation) {
      const duration = Number(options.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration);
      const sensorTracking = options.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking;
      parts.push(`Irrigation ${duration}s${sensorTracking ? ', sensor-guided' : ', schedule only'}`);
    }
    if (hasFan || hasHumidity) {
      parts.push('Immediate trigger for fans/humidifiers');
    }

    return parts.join(' · ') || 'Immediate device trigger';
  }

  return label || '';
}

export function describeQuickActionDeviceDetail(recipe, outcome, action) {
  if (!outcome || !action) return null;

  const timezone = outcome.timezone || action.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone;

  if (outcome.lightThresholds?.low != null && outcome.lightThresholds?.high != null) {
    const { startTime, endTime } = convertThresholdsToTimes(
      outcome.lightThresholds.low,
      outcome.lightThresholds.high,
      timezone
    );
    const tzLabel = formatTimezoneLabel(timezone);
    const schedule = `${startTime.replace(/\s+(AM|PM)/i, '$1')} – ${endTime.replace(/\s+(AM|PM)/i, '$1')} ${tzLabel}`;

    if (recipe === QUICK_ACTION_RECIPES.RESTORE_LIGHTS) {
      return outcome.ok
        ? `Restored to ${schedule}`
        : `Target schedule ${schedule}`;
    }

    return outcome.ok
      ? `Updated to ${schedule}`
      : `Target schedule ${schedule}`;
  }

  if (recipe === QUICK_ACTION_RECIPES.RUN_NOW) {
    if (isIrrigationActuator(action.actuatorType)) {
      const duration = outcome.duration ?? action.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration;
      const sensorTracking = outcome.sensorTracking ?? action.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking;
      const mode = sensorTracking ? 'sensor-guided' : 'schedule only';
      return outcome.ok
        ? `Ran ${duration}s (${mode}), tracking set to 1h`
        : `Attempted ${duration}s (${mode})`;
    }

    if (action.actuatorType === 'fan' || action.actuatorType === 'humidity') {
      const deviceLabel = action.actuatorType === 'fan' ? 'Fan' : 'Humidifier';
      return outcome.ok
        ? `${deviceLabel} triggered, tracking set to 1h`
        : `${deviceLabel} trigger attempted`;
    }
  }

  return null;
}

export function buildQuickActionResult({
  label,
  recipe,
  options = {},
  targetActions,
  successfulDevices,
  failedDevices,
  skippedDevices = [],
}) {
  const relevantActuatorTypes = [...new Set(targetActions.map((action) => action.actuatorType))];
  const actionSummary = describeQuickActionSummary(recipe, options, label, targetActions);

  return {
    modalTitle: 'Quick Action',
    success: failedDevices.length === 0,
    changedThresholds: [label || recipe],
    actionSummary,
    sensorMac: 'Quick Actions',
    totalDevices: targetActions.length + skippedDevices.length,
    relevantDevices: targetActions.length,
    successfulDevices,
    failedDevices,
    skippedDevices,
    relevantActuatorTypes,
    missingActuatorTypes: [],
  };
}

export function selectActionsByPreset(preset, actions = []) {
  const selected = new Set(preset?.actionMacs || []);
  return actions.filter((action) => selected.has(action.macAddress));
}

export function selectActionsByTypes(actions = [], actuatorTypes = []) {
  const types = new Set(actuatorTypes);
  return actions.filter((action) => types.has(action.actuatorType));
}

async function runLightExtend(action, sensorCard, options) {
  if (!sensorCard) {
    return { ok: false, reason: 'No paired sensor card found.' };
  }

  const window = getCurrentLightWindow({
    ...DEFAULT_LIGHT_EXTEND_OPTIONS,
    ...options,
    timezone: options?.timezone || action.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone,
  }, sensorCard);
  const thresholds = buildThresholdsFromCard(sensorCard, window.thresholds);
  const result = await publishMQTTStatus(action.macAddress, sensorCard.macAddress, thresholds, action.tracking || 'X');

  return {
    ok: result === 'success',
    reason: result === 'success' ? null : result,
    lightThresholds: window.thresholds,
    timezone: window.timezone,
  };
}

async function runLightRestore(action, sensorCard, snapshot) {
  if (!sensorCard) {
    return { ok: false, reason: 'No paired sensor card found.' };
  }
  if (!snapshot) {
    return { ok: false, reason: 'No saved schedule snapshot.' };
  }

  const lightThresholds = {
    low: snapshot.lightLow ?? sensorCard.lightLow ?? 'X',
    high: snapshot.lightHigh ?? sensorCard.lightHigh ?? 'X',
  };
  const thresholds = buildThresholdsFromCard(sensorCard, lightThresholds);
  const result = await publishMQTTStatus(action.macAddress, sensorCard.macAddress, thresholds, action.tracking || 'X');

  return {
    ok: result === 'success',
    reason: result === 'success' ? null : result,
    lightThresholds,
    timezone: snapshot.timezone || action.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone,
  };
}

async function runImmediateAction(action, sensorCard, options = {}) {
  if (!sensorCard) {
    return { ok: false, reason: 'No paired sensor card found.' };
  }
  if (!supportsImmediateTrigger(action.actuatorType)) {
    return { ok: false, reason: 'This action type does not support immediate runs.' };
  }

  const thresholds = buildThresholdsFromCard(sensorCard);
  const actionSettings = {
    tracking: '2s',
    duration: options.duration ?? action.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration,
    sensorTracking: options.sensorTracking ?? action.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking,
  };

  const safeguardResult = await handleImmediateActionSafeguard(
    action,
    sensorCard,
    () => {},
    thresholds,
    '2s',
    () => {},
    null,
    actionSettings
  );
  const result = typeof safeguardResult === 'object' ? safeguardResult.result : safeguardResult;

  return {
    ok: result === 'success',
    reason: result === 'success' ? null : result,
    tracking: '1h',
    duration: isIrrigationActuator(action.actuatorType) ? actionSettings.duration : action.duration,
    sensorTracking: isIrrigationActuator(action.actuatorType) ? actionSettings.sensorTracking : action.sensorTracking,
  };
}

export async function executeQuickActions({
  recipe,
  label,
  targetActions = [],
  cards = [],
  options = {},
  snapshots = {},
  onActionSuccess,
  onCardSuccess,
}) {
  const successfulDevices = [];
  const failedDevices = [];
  const skippedDevices = [];

  const executeOne = async (action) => {
    const sensorCard = findSensorCardForAction(action, cards);
    let outcome;

    if (recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS) {
      if (action.actuatorType !== 'light') {
        skippedDevices.push({ ...action, name: getActionDisplayName(action), reason: 'Not a light action.' });
        return;
      }
      outcome = await runLightExtend(action, sensorCard, options);
    } else if (recipe === QUICK_ACTION_RECIPES.RESTORE_LIGHTS) {
      if (action.actuatorType !== 'light') {
        skippedDevices.push({ ...action, name: getActionDisplayName(action), reason: 'Not a light action.' });
        return;
      }
      outcome = await runLightRestore(action, sensorCard, snapshots[action.macAddress]);
    } else {
      outcome = await runImmediateAction(action, sensorCard, options);
    }

    const appliedDetail = describeQuickActionDeviceDetail(recipe, outcome, action);

    if (!outcome.ok) {
      failedDevices.push({
        ...action,
        name: getActionDisplayName(action),
        reason: outcome.reason || 'MQTT update failed.',
        appliedDetail,
      });
      return;
    }

    successfulDevices.push({
      ...action,
      name: getActionDisplayName(action),
      appliedDetail,
    });

    if (action.actuatorType === 'light' && sensorCard && outcome.lightThresholds) {
      await onCardSuccess?.({
        ...sensorCard,
        lightLow: outcome.lightThresholds.low,
        lightHigh: outcome.lightThresholds.high,
      });
      await onActionSuccess?.({
        ...action,
        timezone: outcome.timezone,
      });
      return;
    }

    const updatedAction = {
      ...action,
      tracking: outcome.tracking || action.tracking,
    };
    if (outcome.duration !== undefined) updatedAction.duration = outcome.duration;
    if (outcome.sensorTracking !== undefined) updatedAction.sensorTracking = outcome.sensorTracking;
    await onActionSuccess?.(updatedAction);
  };

  const runSequentially =
    recipe === QUICK_ACTION_RECIPES.RUN_NOW ||
    recipe === QUICK_ACTION_RECIPES.EXTEND_LIGHTS ||
    recipe === QUICK_ACTION_RECIPES.RESTORE_LIGHTS;

  if (runSequentially) {
    for (const action of targetActions) {
      await executeOne(action);
    }
  } else {
    await Promise.all(targetActions.map((action) => executeOne(action)));
  }

  return buildQuickActionResult({
    label,
    recipe,
    options,
    targetActions,
    successfulDevices,
    failedDevices,
    skippedDevices,
  });
}

export function buildPresetSnapshot(preset, actions = [], cards = []) {
  return snapshotLightActions(selectActionsByPreset(preset, actions), cards);
}

export { EMPTY_THRESHOLDS };
